import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import UserOpenEntityAgreement from "legal-entities/agreements/open/user-open-entity-agreement";
import { AuthResponse } from "users/_api/auth-email-api-service";
import { AuthContext } from "users/auth/context/auth-context";
import AuthInfoSchema, { AuthInfoJSON, AuthInfoParams } from "users/auth/info/auth-info-schema";
import UserAuthToken from "users/auth/tokens/user-auth-token";
import UserInformation from "users/information/user-information";
import { User } from "users/user";

export class AuthInfo {
  public static readonly EXAMPLE: AuthInfo = AuthInfo.create({
    authToken: UserAuthToken.EXAMPLE,
    userInformation: UserInformation.EXAMPLE,
  });

  public static create(values: AuthInfoParams): AuthInfo {
    const parsed = AuthInfoSchema.validate(values);
    return new AuthInfo(parsed);
  }

  public static fromJSON(json: unknown): AuthInfo | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const params = AuthInfoSchema.deserialize(json);
    return new AuthInfo(params);
  }

  /**
   * Parses the login response contract that every login context returns.
   *
   * Email login, password-reset confirmation, super-user impersonation, officer- and
   * member-invitation acceptance and entity-agreement acceptance all post to different endpoints
   * and all receive `{ authToken, userInformation }`. They read it here rather than each reaching
   * for the schema, so there is one place where the shape of a login response is known.
   */
  public static parseAuthResponse(payload: unknown): AuthResponse {
    return new AuthInfo(AuthInfoSchema.deserialize(payload)).toAuthResponse();
  }

  public readonly authToken: UserAuthToken;
  public readonly userInformation: UserInformation;

  public get user(): User {
    return User.create({
      id: this.userInformation.id,
      name: this.userInformation.name,
      email: this.userInformation.email,
      legalEntityId: this.userInformation.legalEntityId,
      companyName: this.userInformation.companyName,
      isSuperUser: this.userInformation.isSuperUser,
      individualId: this.userInformation.individualId,
      hasSigningAuthority: this.userInformation.hasSigningAuthority,
      hasEntityWideNotificationsAccess: this.userInformation.hasEntityWideNotificationsAccess,
      hasMemberPermissionsAccess: this.userInformation.hasMemberPermissionsAccess,
      onboardingWork: this.userInformation.onboardingWork,
    });
  }

  public get context(): AuthContext {
    return AuthContext.create(this.userInformation);
  }

  public get openEntityAgreements(): UserOpenEntityAgreement[] {
    return this.userInformation.openEntityAgreements.map((data) => {
      return UserOpenEntityAgreement.create(data);
    });
  }

  public constructor(params: AuthInfoParams) {
    this.authToken = UserAuthToken.create(params.authToken);
    this.userInformation = UserInformation.create(params.userInformation);
  }

  public toJSON(): AuthInfoJSON {
    return AuthInfoSchema.serialize(this);
  }

  public toAuthResponse(): AuthResponse {
    return {
      authToken: this.authToken,
      user: this.user,
      context: this.context,
      openEntityAgreements: this.openEntityAgreements,
    };
  }

  public isEqualTo(other: AuthInfo): boolean {
    return this.authToken.isEqualTo(other.authToken) && this.userInformation.isEqualTo(other.userInformation);
  }

  public toString(): string {
    return `AuthInfo { 
      authToken: ${this.authToken.toString()},
      userInformation: ${this.userInformation.toString()}
    }`;
  }
}
